/* =====================================================
   afk-audio — product page demos
   Positional sensing, audience-side animation modes,
   config-app mockup, sticky add-to-cart reveal.
   The configurable-zones demo reuses [data-configurator]
   from theme.js. Self-contained; safe to include from
   multiple sections (guarded against double-init).
   ===================================================== */
(function () {
  'use strict';
  if (window.__afkProductDemos) return;
  window.__afkProductDemos = true;

  var PALETTE = ['#FF2D2D', '#FF7A00', '#FFB800', '#00E676', '#00E5FF', '#2979FF', '#B14BFF', '#FF2E9A'];

  // Demo loops run only while their block is (nearly) on screen, the tab is
  // visible and the visitor has not asked for reduced motion.
  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  function whenVisible(root, start, stop) {
    var on = false, running = false;
    function sync() {
      var want = on && !document.hidden && !REDUCE.matches;
      if (want && !running) { running = true; start(); }
      else if (!want && running) { running = false; stop(); }
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (e) { on = e[e.length - 1].isIntersecting; sync(); }, { rootMargin: '100px 0px' }).observe(root);
    } else {
      on = true;
    }
    document.addEventListener('visibilitychange', sync);
    if (REDUCE.addEventListener) REDUCE.addEventListener('change', sync); else if (REDUCE.addListener) REDUCE.addListener(sync);
    sync();
  }

  // complementary colour (hue + 180°, same saturation and lightness), as drumbeam shows the hit position in a zone
  function complement(hex) {
    var m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
    if (!m) return '#ffffff';
    var r = parseInt(m[1], 16) / 255, g = parseInt(m[2], 16) / 255, b = parseInt(m[3], 16) / 255;
    var mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2, d = mx - mn, h = 0, s = 0;
    if (d) {
      s = d / (1 - Math.abs(2 * l - 1));
      h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
      h *= 60;
    }
    h = (h + 180 + 360) % 360;
    var c = (1 - Math.abs(2 * l - 1)) * s, x = c * (1 - Math.abs((h / 60) % 2 - 1)), o = l - c / 2;
    var rgb = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
    return '#' + rgb.map(function (v) { return ('0' + Math.round((v + o) * 255).toString(16)).slice(-2); }).join('');
  }

  function paintLedBar(el, zones, activeIndex, onClick) {
    var row = el.querySelector('.led-bar__row');
    if (!row) {
      row = document.createElement('div');
      row.className = 'led-bar__row';
      el.appendChild(row);
    }
    row.innerHTML = '';
    zones.forEach(function (z, zi) {
      for (var i = 0; i < z.n; i++) {
        var led = document.createElement('span');
        led.className = 'led-bar__led' + (zi === activeIndex ? ' is-active' : '');
        led.style.background = z.c;
        led.style.boxShadow = zi === activeIndex
          ? '0 0 10px ' + z.c + ', 0 0 22px ' + z.c
          : '0 0 4px ' + z.c;
        if (onClick) {
          (function (idx) { led.addEventListener('click', function () { onClick(idx); }); })(zi);
          el.classList.add('led-bar--clickable');
        }
        row.appendChild(led);
      }
      if (zi < zones.length - 1) {
        var gap = document.createElement('span');
        gap.className = 'led-bar__gap';
        row.appendChild(gap);
      }
    });
    row.style.height = '20px';
  }

  /* ---------- positional sensing demo ---------- */
  function initPositionDemos() {
    document.querySelectorAll('[data-demo-position]').forEach(function (root) {
      if (root.dataset.afkInit) return;
      root.dataset.afkInit = '1';
      var bar = root.querySelector('.led-bar');
      var dot = root.querySelector('.pdemo__dot');
      var posEl = root.querySelector('[data-pos-value]');
      var ccEl = root.querySelector('[data-cc-value]');
      var areaEl = root.querySelector('[data-filter-area]');
      var curveEl = root.querySelector('[data-filter-curve]');
      var cutEl = root.querySelector('[data-filter-cut]');
      var knobEl = root.querySelector('[data-filter-knob]');
      if (!bar || !dot) return;
      // zone mode (data-leds / data-led-color): one real zone instead of the generic 60-LED bar, seen from the
      // player's side (homepage: the outer zone from the clip). As on the bar, the start of the neighbouring zone and
      // a dark LED come first, on the left; only the zone itself is played. As on drumbeam, a hit lights the whole
      // zone and the position shows as one LED in the complementary colour.
      var ledN = parseInt(root.dataset.leds, 10) || 60, zoneMode = !!root.dataset.leds, ledC = root.dataset.ledColor || '#00E5FF';
      paintLedBar(bar, zoneMode ? [{ c: ledC, n: Math.max(3, Math.round(ledN / 4)) }, { c: ledC, n: ledN }] : [{ c: ledC, n: ledN }], zoneMode ? 1 : 0);
      if (zoneMode) root.classList.add('pdemo--zone');
      var zoneLeds = zoneMode ? [].slice.call(bar.querySelectorAll('.led-bar__led.is-active')) : [];
      var compC = complement(ledC), posIdx = -1;
      function flash() { if (!zoneMode) return; bar.classList.remove('is-hit'); void bar.offsetWidth; bar.classList.add('is-hit'); }

      // Low-pass response on a logarithmic frequency axis (like a real filter
      // plot): flat passband, then a steep 24 dB/oct rolloff past the cutoff.
      // Because the axis is log-f, moving the cutoff just slides the same shape
      // left/right — the curve keeps its form instead of warping.
      var W = 300, BASE = 92, TOP = 8, CEIL = 6, FLOOR_DB = -60, Q = 2;
      var LOGMIN = Math.log(20) / Math.LN10;                 // 20 Hz
      var SPAN = (Math.log(20000) / Math.LN10) - LOGMIN;     // up to 20 kHz
      function yAtDb(db) {
        if (db > CEIL) db = CEIL;
        if (db < FLOOR_DB) db = FLOOR_DB;
        return TOP + ((CEIL - db) / (CEIL - FLOOR_DB)) * (BASE - TOP);
      }
      // Resonant low-pass: a gentle peak at the cutoff, then a steep 24 dB/oct
      // roll-off. The line stops where it reaches the floor instead of riding
      // along the bottom of the plot.
      function renderFilter(posPct) {
        var fcLog = LOGMIN + (0.08 + (posPct / 100) * 0.84) * SPAN;
        var fc = Math.pow(10, fcLog);
        var d = '', N = 120, lastX = 0;
        for (var i = 0; i <= N; i++) {
          var x = i / N * W;
          var f = Math.pow(10, LOGMIN + (x / W) * SPAN);
          var w = f / fc, w2 = w * w;
          var inv = (Math.pow(1 - w2, 2) + w2 / (Q * Q)) * (1 + Math.pow(w, 4));
          var db = isFinite(inv) ? -10 * (Math.log(inv) / Math.LN10) : FLOOR_DB - 1;
          var y = yAtDb(db), floored = y >= BASE;
          if (floored) y = BASE;
          d += (i === 0 ? 'M' : 'L') + x.toFixed(1) + ' ' + y.toFixed(1) + ' ';
          lastX = x;
          if (floored) break;
        }
        if (curveEl) curveEl.setAttribute('d', d.trim());
        if (areaEl) areaEl.setAttribute('d', d + 'L' + lastX.toFixed(1) + ' ' + BASE + ' L0 ' + BASE + ' Z');
        var cutX = ((fcLog - LOGMIN) / SPAN * W).toFixed(1);
        var cutY = yAtDb(20 * (Math.log(Q) / Math.LN10) - 3.0103).toFixed(1);  // resonant peak at cutoff
        if (cutEl) { cutEl.setAttribute('x1', cutX); cutEl.setAttribute('x2', cutX); }
        if (knobEl) { knobEl.setAttribute('cx', cutX); knobEl.setAttribute('cy', cutY); }
      }

      var pos = 40, dir = 1;
      var track = root.querySelector('.pdemo__track');
      // data-invert: the zone is mapped the other way round (right end closes the filter), as in the
      // homepage clip, so dot, curve and sound agree with what the stick does there
      var invert = root.dataset.invert === 'true';
      // zone mode: 0 and 100 % sit on the zone's first and last LED (measured), not on the track's edges
      var span = null;
      function measureZone() {
        span = null;
        if (!zoneMode || !track) return;
        var tr = track.getBoundingClientRect();
        if (zoneLeds.length < 2 || !tr.width) return;
        var a = zoneLeds[0].getBoundingClientRect(), b = zoneLeds[zoneLeds.length - 1].getBoundingClientRect();
        span = { x0: (a.left + a.width / 2 - tr.left) / tr.width, x1: (b.left + b.width / 2 - tr.left) / tr.width };
      }
      function toTrack(p) { return span ? (span.x0 + p / 100 * (span.x1 - span.x0)) * 100 : p; }
      function fromTrack(f) { return span ? (f - span.x0) / (span.x1 - span.x0) * 100 : f * 100; }
      function paint() {
        var cut = invert ? 100 - pos : pos;
        if (zoneMode && !span) measureZone();
        dot.style.left = toTrack(pos) + '%';
        if (zoneLeds.length) {
          var idx = Math.round(pos / 100 * (zoneLeds.length - 1));
          if (idx !== posIdx) { if (zoneLeds[posIdx]) zoneLeds[posIdx].style.background = ledC; zoneLeds[idx].style.background = compC; posIdx = idx; }
        }
        if (posEl) posEl.textContent = Math.round(pos) + '%';
        if (ccEl) ccEl.textContent = String(Math.round(cut * 1.27)).padStart(3, '0');
        if (track) { track.setAttribute('aria-valuenow', String(Math.round(pos))); track.setAttribute('aria-valuetext', Math.round(pos) + '%'); }
        renderFilter(cut);
      }
      // Kyrill 05.10.2026: tap or drag on the bar sets the position by hand. The sweep pauses
      // while you play with it and picks up again from there after a few idle seconds.
      var manualUntil = 0, IDLE = 6000, followUntil = 0;
      function step() {
        if (Date.now() < manualUntil || Date.now() < followUntil) return;
        pos += dir * 1.5;
        if (pos > 90) dir = -1;
        if (pos < 10) dir = 1;
        paint();
      }
      function setManual(p) {
        pos = Math.max(0, Math.min(100, p));
        manualUntil = Date.now() + IDLE;
        root.classList.add('is-used');
        paint();
      }
      if (track) {
        var dragging = false;
        var fromEvent = function (e) { var r = track.getBoundingClientRect(); if (zoneMode) measureZone(); return r.width ? fromTrack((e.clientX - r.left) / r.width) : pos; };
        if (zoneMode) window.addEventListener('resize', function () { span = null; paint(); }, { passive: true });
        track.addEventListener('pointerdown', function (e) {
          dragging = true;
          if (track.setPointerCapture) { try { track.setPointerCapture(e.pointerId); } catch (err) { /* old browsers */ } }
          setManual(fromEvent(e));
          flash();
        });
        track.addEventListener('pointermove', function (e) { if (dragging) setManual(fromEvent(e)); });
        var end = function () { dragging = false; };
        track.addEventListener('pointerup', end);
        track.addEventListener('pointercancel', end);
        track.addEventListener('keydown', function (e) {
          var k = e.key, d = k === 'ArrowRight' || k === 'ArrowUp' ? 5 : k === 'ArrowLeft' || k === 'ArrowDown' ? -5 : 0;
          if (k === 'Home') { e.preventDefault(); setManual(0); return; }
          if (k === 'End') { e.preventDefault(); setManual(100); return; }
          if (d) { e.preventDefault(); setManual(pos + d); }
        });
      }
      // a clip next to the demo can drive it (sections/feature-statement.liquid): follow(p, hit) sets the
      // position from the clip's timeline; a hand on the demo wins for IDLE ms, the own sweep waits meanwhile
      root.__pdemo = {
        follow: function (p, hit) {
          if (Date.now() < manualUntil) return false;
          followUntil = Date.now() + 1500;
          pos = Math.max(0, Math.min(100, p));
          paint();
          if (hit) { flash(); dot.classList.remove('is-hit'); void dot.offsetWidth; dot.classList.add('is-hit'); }
          return true;
        }
      };
      var t = 0;
      whenVisible(root, function () { t = setInterval(step, 60); }, function () { clearInterval(t); });
      paint();  // first frame at once, so the readouts agree even while the loop is paused (reduced motion)
    });
  }

  /* ---------- audience-side animation modes demo ----------
     Self-running: a simulated stick hit drives whichever mode is active, so
     each mode reads like it does on the real bar.
       highlight — zones lit steady in their colors; struck zone flares
       flash     — whole bar flashes the hit color, then fades
       sparkle   — random LEDs twinkle across the bar in varying colors
       beam      — a pulse radiates outward from the hit point
       off       — dark                                                      */
  function initModeDemos() {
    document.querySelectorAll('[data-demo-modes]').forEach(function (root) {
      if (root.dataset.afkInit) return;
      root.dataset.afkInit = '1';
      var row = root.querySelector('.pdemo__strip-row');
      if (!row) return;
      var COUNT = 60, ZONES = 8, GAP = 2;
      var leds = [];
      row.innerHTML = '';
      for (var i = 0; i < COUNT; i++) { var s = document.createElement('span'); row.appendChild(s); leds.push(s); }

      // zone map with dark dividers between zones (drives the highlight mode)
      var zoneOf = new Int8Array(COUNT);
      (function () {
        var lit = COUNT - (ZONES - 1) * GAP, base = Math.floor(lit / ZONES), rem = lit - base * ZONES, idx = 0;
        for (var z = 0; z < ZONES; z++) {
          for (var k = 0, w = base + (z < rem ? 1 : 0); k < w; k++) zoneOf[idx++] = z;
          if (z < ZONES - 1) for (var g = 0; g < GAP; g++) zoneOf[idx++] = -1;
        }
        while (idx < COUNT) zoneOf[idx++] = -1;
      })();
      function zoneColor(z) { return PALETTE[z % PALETTE.length]; }
      function zoneCenter(z) { var f = -1, l = -1; for (var i = 0; i < COUNT; i++) if (zoneOf[i] === z) { if (f < 0) f = i; l = i; } return (f + l) / 2; }

      var modes = ['highlight', 'flash', 'sparkle', 'beam', 'off'];
      var mode = modes.indexOf('beam');
      var bright = new Float32Array(COUNT);
      var color = new Array(COUNT);
      var lastV = new Float32Array(COUNT), lastC = new Array(COUNT);
      for (var i = 0; i < COUNT; i++) { color[i] = zoneColor(zoneOf[i] < 0 ? 0 : zoneOf[i]); lastV[i] = -1; }
      var beams = [], sparks = [], flare = null, lastHit = 0, hitGap = 600;
      // animation speed multiplier from the block setting (data-speed)
      var SP = parseFloat(root.dataset.speed) || 1;
      if (SP <= 0) SP = 1;
      var decayF = Math.pow(0.90, SP);

      function set(led, c, v) {
        // unlit LEDs are light grey like the real diffuser (--dev-off); a fading LED blends from its colour
        // towards that grey instead of towards black, so nothing on the bar ever reads as a dark dot
        if (v <= 0.03) {
          led.style.background = 'var(--dev-off, #8E8E94)';
          led.style.boxShadow = 'none';
          led.style.opacity = '1';
        } else {
          led.style.background = 'color-mix(in srgb, ' + c + ' ' + Math.round(35 + 65 * v) + '%, var(--dev-off, #8E8E94))';
          led.style.boxShadow = '0 0 ' + (3 + v * 7).toFixed(1) + 'px ' + c + ', 0 0 ' + (v * 15).toFixed(1) + 'px ' + c;
          led.style.opacity = '1';
        }
      }

      function hit(now) {
        var z = Math.floor(Math.random() * ZONES);
        var c = zoneColor(z);
        var center = zoneCenter(z);
        var m = modes[mode];
        if (m === 'flash') {
          for (var i = 0; i < COUNT; i++) { bright[i] = 1; color[i] = c; }
        } else if (m === 'sparkle') {
          // one LED runs outward in each direction from the hit point
          sparks.push({ c: c, center: center, t: now, dir: -1 });
          sparks.push({ c: c, center: center, t: now, dir: 1 });
          if (sparks.length > 16) sparks.splice(0, sparks.length - 16);
        } else if (m === 'beam') {
          beams.push({ c: c, center: center, t: now });
          if (beams.length > 4) beams.shift();
        } else if (m === 'highlight') {
          flare = { z: z, t: now };
        }
      }

      function frame(now) {
        var m = modes[mode], i;
        if (m !== 'off' && now - lastHit > hitGap) { lastHit = now; hitGap = (480 + Math.random() * 380) / SP; hit(now); }

        if (m === 'highlight') {
          var fz = -1, fb = 0;
          if (flare) { fb = Math.max(0, 1 - (now - flare.t) * SP / 360); fz = flare.z; }
          for (i = 0; i < COUNT; i++) {
            var hz = zoneOf[i];
            if (hz < 0) { bright[i] = 0; continue; }
            color[i] = zoneColor(hz);
            bright[i] = (hz === fz) ? 0.42 + 0.58 * fb : 0.42;
          }
        } else if (m === 'beam') {
          for (i = 0; i < COUNT; i++) bright[i] = 0;
          for (var bi = 0; bi < beams.length; bi++) {
            var bm = beams[bi];
            var age = (now - bm.t) / 1000;
            var life = Math.max(0, 1 - age * SP / 0.7);
            if (life <= 0) continue;
            var radius = age * 55 * SP;
            for (i = 0; i < COUNT; i++) {
              var d = Math.abs(i - bm.center);
              var v = (d <= radius ? 1 - 0.35 * d / (radius + 1) : Math.max(0, 1 - (d - radius) / 2.5)) * life;
              if (v > bright[i]) { bright[i] = v; color[i] = bm.c; }
            }
          }
        } else if (m === 'sparkle') {
          var SPEED = 17 * SP, RANGE = 7;
          for (i = 0; i < COUNT; i++) bright[i] = 0;
          var alive = [];
          for (var si = 0; si < sparks.length; si++) {
            var sp = sparks[si];
            var dist = (now - sp.t) / 1000 * SPEED;
            if (dist > RANGE) continue;
            alive.push(sp);
            var life = 1 - dist / RANGE, head = sp.center + sp.dir * dist;
            for (i = 0; i < COUNT; i++) {
              var gap = Math.abs(i - head);
              if (gap > 2) continue;
              var v = Math.max(0, 1 - gap / 1.3) * life;
              if (v > bright[i]) { bright[i] = v; color[i] = sp.c; }
            }
          }
          sparks = alive;
        } else if (m === 'off') {
          for (i = 0; i < COUNT; i++) bright[i] = 0;
        } else {
          for (i = 0; i < COUNT; i++) bright[i] *= decayF;
        }

        for (i = 0; i < COUNT; i++) {
          if (Math.abs(bright[i] - lastV[i]) > 0.02 || color[i] !== lastC[i]) {
            set(leds[i], color[i], bright[i]);
            lastV[i] = bright[i]; lastC[i] = color[i];
          }
        }
      }

      // ~30 fps is plenty for an LED simulation; runs only while on screen
      var raf = 0, lastT = 0;
      function loop(now) {
        raf = requestAnimationFrame(loop);
        if (now - lastT < 33) return;
        lastT = now;
        frame(now);
      }
      whenVisible(root, function () { raf = requestAnimationFrame(loop); }, function () { cancelAnimationFrame(raf); });

      // reduced motion: one still frame of the chosen mode, a moment after a hit
      function still() {
        var now = performance.now();
        lastHit = now;
        hit(now);
        frame(now + 160);
      }

      var modeBtns = root.querySelectorAll('[data-mode]');
      function pressed() {
        modeBtns.forEach(function (b, j) {
          b.classList.toggle('is-active', j === mode);
          b.setAttribute('aria-pressed', j === mode ? 'true' : 'false');
        });
      }
      if (REDUCE.matches) { mode = modes.indexOf('highlight'); still(); }
      pressed();
      if (REDUCE.addEventListener) REDUCE.addEventListener('change', function () { if (REDUCE.matches) still(); });

      modeBtns.forEach(function (btn, i) {
        btn.addEventListener('click', function () {
          mode = i;
          beams = []; sparks = []; flare = null; lastHit = 0;
          for (var j = 0; j < COUNT; j++) bright[j] = 0;
          pressed();
          if (REDUCE.matches) still();
        });
      });
    });
  }

  /* ---------- config app mockup ---------- */
  function initConfigMockups() {
    document.querySelectorAll('[data-config-mockup]').forEach(function (root) {
      if (root.dataset.afkInit) return;
      root.dataset.afkInit = '1';
      var bar = root.querySelector('.led-bar');
      var detail = root.querySelector('[data-cfg-detail]');
      if (!bar) return;
      var labels = ['kick', 'snare', 'tom 1', 'hat', 'ride', 'crash'];
      var notes = [36, 38, 41, 42, 51, 49];
      var zones = labels.map(function (l, i) { return { c: PALETTE[i], n: 8, label: l }; });
      var active = 3;

      function paint() {
        paintLedBar(bar, zones, active, function (idx) { active = idx; paint(); });
        root.querySelectorAll('[data-cfg-zone]').forEach(function (chip, i) {
          var isActive = i === active;
          chip.style.background = isActive ? 'var(--bg-graphite)' : 'transparent';
          chip.style.borderColor = isActive ? PALETTE[i] + '66' : '';
          chip.style.color = isActive ? PALETTE[i] : '';
        });
        if (detail) {
          detail.querySelector('[data-cfg-name]').textContent = 'zone ' + (active + 1) + ' · ' + labels[active];
          detail.querySelector('[data-cfg-name]').style.color = PALETTE[active];
          detail.querySelector('[data-cfg-note]').textContent = notes[active];
        }
      }

      root.querySelectorAll('[data-cfg-zone]').forEach(function (chip, i) {
        var dot = chip.querySelector('.dot');
        if (dot) {
          dot.style.background = PALETTE[i];
          dot.style.boxShadow = '0 0 6px ' + PALETTE[i];
        }
        chip.addEventListener('click', function () { active = i; paint(); });
      });
      paint();
    });
  }

  /* ---------- sticky add-to-cart ----------
     Phones (≤880, portrait height): visible whenever the buy box's own button
     is off screen, above or below, and hidden while that button, the footer
     or Shopify's cookie banner is in view; the first reveal waits until the
     banner has had its chance to appear (up to 2 s after load), so the bar
     never slides in and straight out again. Wider screens and short
     landscape: visible once that button has scrolled away under the header
     (as before), read from the button's position on every scroll, since an
     instant jump past it gives the observer nothing to report.
     While hidden the bar is inert (out of the Tab order and the a11y tree).
     On a PDP the bar's button submits the buy-box form, so theme.js adds the
     variant, the quantity and any ticked add-on. Form and button are looked
     up again after an editor re-render of main-product.                   */
  function buyForm() { return document.querySelector('[data-section="main-product"] form[data-ajax-cart]'); }
  function buyButton(form) { return form && form.querySelector('[type="submit"]'); }
  // the cookie banner is only read here, never styled
  function consentUp() {
    var cb = document.getElementById('shopify-pc__banner');
    return !!(cb && cb.getClientRects().length && cb.getBoundingClientRect().height > 0);
  }

  function initStickyAtc() {
    var bar = document.querySelector('.sticky-atc');
    if (!bar || bar.dataset.afkInit) return;
    bar.dataset.afkInit = '1';
    var buyBtn = buyButton(buyForm());
    var footer = document.querySelector('.site-footer');
    var phone = window.matchMedia('(max-width: 880px) and (min-height: 500px)');
    var narrow = window.matchMedia('(max-width: 880px)');
    var hh = function () { var h = document.querySelector('.site-header'); return h ? h.getBoundingClientRect().bottom : 0; };
    var btnIn = false, footIn = false, top = 0;
    var held = phone.matches;   // phones: no reveal before the cookie banner check has settled
    function show(on) {
      bar.classList.toggle('is-visible', on);
      if (bar.inert !== !on) bar.inert = !on;
    }
    // wider screens: the button's bottom has gone under the header
    function above() { return !!buyBtn && buyBtn.getBoundingClientRect().bottom < top; }
    function paint() { show(phone.matches ? (!held && !btnIn && !footIn && !consentUp()) : above()); }
    bar.inert = !bar.classList.contains('is-visible');

    if ('IntersectionObserver' in window && buyBtn) {
      // the fixed header covers the top strip and the bar itself the bottom one:
      // the button only counts as in view between the two
      top = Math.max(0, Math.round(hh()));
      var bottom = bar.offsetHeight;
      var io = new IntersectionObserver(function (es) {
        btnIn = es[es.length - 1].isIntersecting;
        paint();
      }, { rootMargin: '-' + top + 'px 0px -' + bottom + 'px 0px' });
      io.observe(buyBtn);
      if (footer) new IntersectionObserver(function (es) { footIn = es[es.length - 1].isIntersecting; paint(); }).observe(footer);
      if (phone.addEventListener) phone.addEventListener('change', paint); else if (phone.addListener) phone.addListener(paint);
      // an instant jump (anchor link under reduced motion) can pass the button without an
      // observer callback: repaint once per frame while scrolling
      var sq = 0;
      window.addEventListener('scroll', function () {
        if (sq) return;
        sq = requestAnimationFrame(function () { sq = 0; paint(); });
      }, { passive: true });
      // theme editor: a re-rendered main-product brings a new button, watch that one instead
      document.addEventListener('shopify:section:load', function () {
        var nb = buyButton(buyForm());
        if (!nb || nb === buyBtn) return;
        io.disconnect();
        buyBtn = nb;
        io.observe(nb);
      });

      // first visit: Shopify appends its cookie banner to <body> around load; while it is up the
      // phone bar waits, so its edges never peek out beside the dialog. The banner is only watched,
      // never styled: its own style/class changes (it hides itself with display:none), a tap inside
      // it and visitorConsentCollected repaint, and a 1 s check runs while it is up (capped at 2 min,
      // restarted by any of those, so a late Accept or Decline still brings the bar back).
      var ticks = 0, iv = 0, seen = false;
      var release = function () {
        if (!held) return;
        held = false;
        paint();
      };
      var watch = function () {
        if (consentUp()) held = false;   // the banner is up: from now on it alone keeps the bar hidden
        paint();
        if (iv || !consentUp()) return;
        iv = setInterval(function () {
          paint();
          if (!consentUp() || ++ticks >= 120) { clearInterval(iv); iv = 0; ticks = 0; }
        }, 1000);
      };
      var rewatch = function () { ticks = 0; watch(); };
      var found = function (cb) {
        if (seen) return;
        seen = true;
        if (window.MutationObserver) new MutationObserver(rewatch).observe(cb, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
        document.addEventListener('click', function (e) {
          if (e.target && e.target.closest && e.target.closest('#shopify-pc__banner')) rewatch();
        }, true);
        watch();
      };
      document.addEventListener('visitorConsentCollected', rewatch);
      // no banner up by 2 s after load (consent given on an earlier visit, or none needed): reveal
      var arm = function () { setTimeout(release, 2000); };
      if (document.readyState === 'complete') arm(); else window.addEventListener('load', arm);
      var cb0 = document.getElementById('shopify-pc__banner');
      if (cb0) {
        found(cb0);
      } else if (window.MutationObserver) {
        var cmo = new MutationObserver(function () {
          var cb = document.getElementById('shopify-pc__banner');
          if (!cb) return;
          cmo.disconnect();
          found(cb);
        });
        cmo.observe(document.body, { childList: true });
      }
    } else {
      // fallback: visible once the PDP's own button has scrolled out above the viewport
      var update = function () {
        var b = buyButton(buyForm());
        show(b ? b.getBoundingClientRect().bottom < 0 : window.scrollY > 720);
      };
      window.addEventListener('scroll', update, { passive: true });
      update();
    }

    // phones: the status keeps only its first part (e.g. 'in stock') when the whole line would be cut off
    var status = bar.querySelector('.sticky-atc__info .fineprint');
    function fitStatus() {
      if (!status) return;
      status.classList.remove('is-short');
      if (!narrow.matches || status.scrollWidth <= status.clientWidth + 1) return;
      if (!status.querySelector('.sticky-atc__status-more')) {
        var node = status.lastChild, text = node && node.nodeType === 3 ? node.nodeValue : '', cut = text.indexOf('·');
        var head = cut > 0 ? text.slice(0, cut).replace(/\s+$/, '') : '';
        if (!head) return;
        var more = document.createElement('span');
        more.className = 'sticky-atc__status-more';
        more.textContent = text.slice(head.length);
        node.nodeValue = head;
        status.appendChild(more);
      }
      status.classList.add('is-short');
    }
    fitStatus();
    var ft = 0;
    window.addEventListener('resize', function () { clearTimeout(ft); ft = setTimeout(fitStatus, 150); }, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitStatus);

    var sb = bar.querySelector('[data-sticky-submit]');
    if (!sb) return;
    sb.addEventListener('click', function () {
      if (sb.getAttribute('aria-busy') === 'true') return;   // no second unit on double tap
      var form = buyForm(), btn = buyButton(form);           // looked up now: the editor may have re-rendered the buy box
      if (!btn) {                                            // no buy box on this page: plain cart permalink
        if (sb.dataset.variantId) window.location.href = '/cart/add?id=' + sb.dataset.variantId + '&quantity=1';
        return;
      }
      if (btn.disabled) { btn.scrollIntoView({ block: 'center' }); return; }        // e.g. an unavailable option is picked
      if (form.checkValidity && !form.checkValidity()) { form.reportValidity(); return; } // e.g. quantity above stock
      var label = sb.textContent, timer = 0, mo = null;
      var reset = function () {
        clearTimeout(timer);
        document.removeEventListener('afk:cart', reset);
        if (sb.getAttribute('aria-busy') !== 'true') return;
        sb.removeAttribute('aria-busy');
        sb.disabled = false;
        sb.textContent = label;
      };
      var finished = function () {
        mo.disconnect();
        reset();
        // a failed add leaves theme.js's message right after the form's own button, far above: go there
        var st = btn.nextElementSibling;
        if (st && st.hasAttribute('data-add-status')) {
          btn.scrollIntoView({ block: 'center' });
          try { btn.focus({ preventScroll: true }); } catch (e) {}
        }
      };
      sb.setAttribute('aria-busy', 'true');
      sb.disabled = true;
      sb.textContent = sb.dataset.busyLabel || 'adding…';
      timer = setTimeout(reset, 5000);
      if (form.requestSubmit) form.requestSubmit(); else btn.click();
      // theme.js disables the form's own button while it adds and enables it again once the add has
      // succeeded or failed (afk:cart fires in both cases, so it cannot tell them apart)
      if (window.MutationObserver && btn.disabled) {
        mo = new MutationObserver(function () { if (!btn.disabled) finished(); });
        mo.observe(btn, { attributes: true, attributeFilter: ['disabled'] });
      } else {
        document.addEventListener('afk:cart', reset);
      }
    });
  }

  /* ---------- tap hint on the interactive demos ----------
     Kyrill 05.10.2026: the same small finger icon as on the homepage configurator.
     It taps a few times once the panel is on screen and goes away after the first
     interaction with the panel. */
  function initTapHints() {
    document.querySelectorAll('[data-tap-hint]').forEach(function (root) {
      if (root.dataset.tapInit) return;
      root.dataset.tapInit = '1';
      var used = function () { root.classList.add('is-used'); };
      root.addEventListener('pointerdown', used);
      root.addEventListener('keydown', used);
      if (!('IntersectionObserver' in window)) { root.classList.add('is-seen'); return; }
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (en) { if (en.isIntersecting) { root.classList.add('is-seen'); io.disconnect(); } });
      }, { threshold: 0.6 });
      io.observe(root);
    });
  }

  function boot() {
    initTapHints();
    initPositionDemos();
    initModeDemos();
    initConfigMockups();
    initStickyAtc();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
